import { openDB, IDBPDatabase } from 'idb';

export interface IndexDbTableConfig {
  dbName: string;
  storeName: string;
  keyPath: string;
  version?: number;
}

// ── Table-scoped event types ──────────────────────────────────────────────────

export interface IndexDbTableInitFailedEvent {
  error: Error;
}

export interface IndexDbTableRecordEvent<T> {
  record: T;
}

export interface IndexDbTableDeleteEvent {
  keys: string[];
}

/** All events a table can emit, keyed by event name. */
export interface IndexDbTableEventMap<T = object> {
  initialized: Record<string, never>;
  'init-failed': IndexDbTableInitFailedEvent;
  recordAdded: IndexDbTableRecordEvent<T>;
  recordUpdated: IndexDbTableRecordEvent<T>;
  recordsDeleted: IndexDbTableDeleteEvent;
}

export type IndexDbTableEventType = keyof IndexDbTableEventMap<object>;

// ── Base class ────────────────────────────────────────────────────────────────

/**
 * Abstract base class for a single-store IndexedDB table.
 *
 * To support a new entity type, create a subclass, set `tableId` and
 * `config`, and specialise the generic parameter:
 *
 *   class MyThingTable extends IndexDbTable<MyThing> {
 *     readonly tableId = 'myThings';
 *     protected readonly config = {
 *       dbName: 'my-db', storeName: 'things', keyPath: 'id',
 *     };
 *   }
 *
 * ## Table-scoped events
 * Use `table.on` / `table.off` to subscribe to lifecycle events emitted by
 * the table itself (e.g. `initialized`, `init-failed`).  IndexDbModule
 * subscribes in `preload()` and re-broadcasts as generic module events with
 * `tableId` in the payload.
 *
 * Subclasses can call the protected `emit()` to raise additional events
 * as the schema grows.
 *
 * ## Design principle
 * A record's presence means it has not yet been delivered.
 * Records are deleted on confirmed delivery — no status field, no pruning job.
 */
export abstract class IndexDbTable<T extends object = any> {
  /** Unique identifier used in IndexDbModule event payloads. */
  abstract readonly tableId: string;

  protected abstract readonly config: IndexDbTableConfig;

  // ── Event handler registry (tracks on/off registrations) ───────────────
  private readonly eventHandlers = new Map<
    IndexDbTableEventType,
    Set<(payload: IndexDbTableEventMap<T>[IndexDbTableEventType]) => void>
  >();

  private db: IDBPDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  private initError: Error | null = null;

  // ── Table-scoped event API ──────────────────────────────────────────────

  on<K extends IndexDbTableEventType>(
    event: K,
    handler: (payload: IndexDbTableEventMap<T>[K]) => void,
  ): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers
      .get(event)!
      .add(
        handler as (
          payload: IndexDbTableEventMap<T>[IndexDbTableEventType],
        ) => void,
      );
  }

  off<K extends IndexDbTableEventType>(
    event: K,
    handler: (payload: IndexDbTableEventMap<T>[K]) => void,
  ): void {
    this.eventHandlers
      .get(event)
      ?.delete(
        handler as (
          payload: IndexDbTableEventMap<T>[IndexDbTableEventType],
        ) => void,
      );
  }

  /**
   * Emits a table-scoped event to all registered eventHandlers.
   * Protected so subclasses can raise additional events beyond `initialization`.
   */
  protected emit<K extends IndexDbTableEventType>(
    event: K,
    payload: IndexDbTableEventMap<T>[K],
  ): void {
    this.eventHandlers.get(event)?.forEach((h) => h(payload));
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────

  /**
   * Opens (or creates) the database and object store.
   * Idempotent — repeated calls are no-ops once the database is open.
   * Concurrent calls are safe: all callers share the same in-flight promise.
   * On failure, the in-flight promise is cleared so `init()` can be retried,
   * and the returned promise rejects with the error.
   * Emits `initialized` on success, `init-failed` on failure.
   */
  init(): Promise<void> {
    if (this.db !== null) return Promise.resolve();
    if (this.initPromise !== null) return this.initPromise;

    const { dbName, storeName, keyPath, version = 1 } = this.config;

    // Wrap openDB in a try/catch so a synchronous throw becomes a rejected
    // promise that can be safely chained. Without this, a synchronous throw
    // inside the IIFE's try block would run the catch handler before the outer
    // `this.initPromise = ...` assignment completes, leaving initPromise set to
    // the (already-resolved) IIFE promise instead of null.
    let dbPromise: Promise<IDBPDatabase>;
    try {
      dbPromise = openDB(dbName, version, {
        upgrade(database) {
          if (!database.objectStoreNames.contains(storeName)) {
            database.createObjectStore(storeName, { keyPath });
          }
        },
      });
    } catch (e) {
      dbPromise = Promise.reject(e);
    }

    this.initPromise = dbPromise
      .then((db) => {
        this.db = db;
        this.emit('initialized', {});
      })
      .catch((rawError) => {
        this.initError =
          rawError instanceof Error ? rawError : new Error(String(rawError));
        this.initPromise = null;
        this.emit('init-failed', { error: this.initError });
        throw this.initError;
      });

    return this.initPromise;
  }

  // ── CRUD ────────────────────────────────────────────────────────────────

  async add(record: T): Promise<void> {
    this.assertAvailable();
    await this.db!.add(this.config.storeName, record);
    this.emit('recordAdded', { record });
  }

  async put(record: T): Promise<void> {
    this.assertAvailable();
    await this.db!.put(this.config.storeName, record);
    this.emit('recordUpdated', { record });
  }

  async getAll(): Promise<T[]> {
    this.assertAvailable();
    return this.db!.getAll(this.config.storeName) as Promise<T[]>;
  }

  /** Deletes records by primary key in a single transaction. */
  async deleteMany(keys: string[]): Promise<void> {
    if (keys.length === 0) return;
    this.assertAvailable();
    const tx = this.db!.transaction(this.config.storeName, 'readwrite');
    await Promise.all([...keys.map((key) => tx.store.delete(key)), tx.done]);
    this.emit('recordsDeleted', { keys });
  }

  /** Closes the IDB connection and releases the database lock. */
  dispose(): void {
    this.db?.close();
    this.db = null;
    this.initPromise = null;
  }

  // ── State accessors ─────────────────────────────────────────────────────

  get isAvailable(): boolean {
    return this.db !== null;
  }

  get error(): Error | null {
    return this.initError;
  }

  private assertAvailable(): void {
    if (!this.db) {
      throw (
        this.initError ??
        new Error('IndexedDB not initialized. Call init() first.')
      );
    }
  }
}
